import {
  Check,
  ChevronDown,
  Globe,
  ListFilter,
  Mail,
  MapPin,
  Search,
  UserRound,
} from 'lucide-react'

import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import type {
  CrmDataset,
  Lead,
} from '../../types/crm'

import { LeadWebsiteLogo } from '../leads/LeadWebsiteLogo'

import {
  DASHBOARD_ACTIVITY_EVENT,
  getAllDashboardActivity,
  getDashboardActivityCategory,
  type DashboardActivityCategory,
  type DashboardActivityEntry,
} from './dashboardActivity'

type ActivityFilter =
  | 'all'
  | 'missing-owner'
  | 'missing-contact'
  | 'missing-website'
  | 'missing-location'

type ActivityRow = {
  entry: DashboardActivityEntry
  lead: Lead | null
  company: string
  owner: string
  website: string
  category:
    | DashboardActivityCategory
    | null
}

const FILTER_OPTIONS = [
  {
    value: 'all' as const,
    label: 'All Activity',
    icon: ListFilter,
  },
  {
    value: 'missing-owner' as const,
    label: 'Missing Owners',
    icon: UserRound,
  },
  {
    value: 'missing-contact' as const,
    label: 'Missing Contact Info',
    icon: Mail,
  },
  {
    value: 'missing-website' as const,
    label: 'Missing Website',
    icon: Globe,
  },
  {
    value: 'missing-location' as const,
    label: 'Missing Location',
    icon: MapPin,
  },
]

function ownerName(
  lead: Lead | null,
): string {
  if (!lead) return 'Not identified'

  const owner =
    lead.opportunity?.name ||
    lead.owner

  if (
    !owner ||
    owner.trim().toLowerCase() ===
      'not identified'
  ) {
    return 'Not identified'
  }

  return owner
}

function dateKey(
  timestamp: number,
): string {
  const date = new Date(timestamp)

  const year = date.getFullYear()
  const month = String(
    date.getMonth() + 1,
  ).padStart(2, '0')

  const day = String(
    date.getDate(),
  ).padStart(2, '0')

  return `${year}-${month}-${day}`
}

function dateHeading(
  timestamp: number,
): string {
  const date = new Date(timestamp)
  const today = new Date()

  const yesterday =
    new Date(today)

  yesterday.setDate(
    today.getDate() - 1,
  )

  if (
    dateKey(timestamp) ===
    dateKey(today.getTime())
  ) {
    return 'TODAY'
  }

  if (
    dateKey(timestamp) ===
    dateKey(yesterday.getTime())
  ) {
    return 'YESTERDAY'
  }

  return date
    .toLocaleDateString(
      'en-US',
      {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      },
    )
    .toUpperCase()
}

function activityTime(
  timestamp: number,
): string {
  return new Date(timestamp)
    .toLocaleTimeString(
      'en-US',
      {
        hour: 'numeric',
        minute: '2-digit',
      },
    )
}

function ActivityCategoryIcon({
  category,
}: {
  category:
    | DashboardActivityCategory
    | null
}) {
  if (category === 'missing-owner') {
    return <UserRound size={14} />
  }

  if (category === 'missing-contact') {
    return <Mail size={14} />
  }

  if (category === 'missing-website') {
    return <Globe size={14} />
  }

  if (category === 'missing-location') {
    return <MapPin size={14} />
  }

  return <ListFilter size={14} />
}

export function ActivityHistory({
  dataset,
}: {
  dataset: CrmDataset
}) {
  const [
    entries,
    setEntries,
  ] = useState<
    DashboardActivityEntry[]
  >(
    () =>
      getAllDashboardActivity(),
  )

  const [
    search,
    setSearch,
  ] = useState('')

  const [
    filter,
    setFilter,
  ] = useState<ActivityFilter>('all')

  const [
    menuOpen,
    setMenuOpen,
  ] = useState(false)

  const menuRef =
    useRef<HTMLDivElement>(null)

  useEffect(() => {
    function refresh() {
      setEntries(
        getAllDashboardActivity(),
      )
    }

    window.addEventListener(
      DASHBOARD_ACTIVITY_EVENT,
      refresh,
    )

    window.addEventListener(
      'storage',
      refresh,
    )

    return () => {
      window.removeEventListener(
        DASHBOARD_ACTIVITY_EVENT,
        refresh,
      )

      window.removeEventListener(
        'storage',
        refresh,
      )
    }
  }, [])

  useEffect(() => {
    if (!menuOpen) return

    function handlePointerDown(
      event: PointerEvent,
    ) {
      if (
        !menuRef.current?.contains(
          event.target as Node,
        )
      ) {
        setMenuOpen(false)
      }
    }

    function handleKeyDown(
      event: KeyboardEvent,
    ) {
      if (event.key === 'Escape') {
        setMenuOpen(false)
      }
    }

    document.addEventListener(
      'pointerdown',
      handlePointerDown,
    )

    document.addEventListener(
      'keydown',
      handleKeyDown,
    )

    return () => {
      document.removeEventListener(
        'pointerdown',
        handlePointerDown,
      )

      document.removeEventListener(
        'keydown',
        handleKeyDown,
      )
    }
  }, [menuOpen])

  const leadMap = useMemo(
    () =>
      new Map(
        dataset.leads.map(
          (lead) => [
            lead.id,
            lead,
          ],
        ),
      ),
    [dataset.leads],
  )

  const rows =
    useMemo<ActivityRow[]>(
      () =>
        entries.map((entry) => {
          const lead =
            leadMap.get(
              entry.leadId,
            ) ?? null

          return {
            entry,
            lead,
            company:
              entry.company,
            owner:
              ownerName(lead),
            website:
              lead?.organization
                ?.website ?? '',
            category:
              getDashboardActivityCategory(
                entry,
              ),
          }
        }),
      [entries, leadMap],
    )

  const visibleRows =
    useMemo(
      () => {
        const query =
          search
            .trim()
            .toLowerCase()

        return rows.filter(
          (row) => {
            if (
              filter !== 'all' &&
              row.category !== filter
            ) {
              return false
            }

            if (!query) {
              return true
            }

            return (
              row.company
                .toLowerCase()
                .includes(query) ||
              row.owner
                .toLowerCase()
                .includes(query)
            )
          },
        )
      },
      [rows, filter, search],
    )

  const groupedRows =
    useMemo(
      () => {
        const groups =
          new Map<
            string,
            ActivityRow[]
          >()

        for (
          const row of visibleRows
        ) {
          const key =
            dateKey(
              row.entry.timestamp,
            )

          const group =
            groups.get(key) ?? []

          group.push(row)

          groups.set(
            key,
            group,
          )
        }

        return Array.from(
          groups.entries(),
        )
      },
      [visibleRows],
    )

  const selectedOption =
    FILTER_OPTIONS.find(
      (option) =>
        option.value === filter,
    ) ?? FILTER_OPTIONS[0]

  const SelectedIcon =
    selectedOption.icon

  return (
    <section
      aria-labelledby="activity-history-title"
      className="activity-history"
    >
      <header className="activity-history-header">
        <div>
          <span>
            Activity history
          </span>

          <h3 id="activity-history-title">
            William's CRM activity
          </h3>
        </div>

        <p>
          Search and review completed
          CRM work by date.
        </p>
      </header>

      <div className="activity-history-toolbar">
        <label className="activity-history-search">
          <Search
            aria-hidden="true"
            size={17}
          />

          <span className="sr-only">
            Search company or owner
          </span>

          <input
            aria-label="Search company or owner"
            onChange={(event) =>
              setSearch(
                event.target.value,
              )
            }
            placeholder="Search company or owner..."
            type="search"
            value={search}
          />
        </label>

        <div
          className="activity-filter"
          ref={menuRef}
        >
          <button
            aria-expanded={menuOpen}
            aria-haspopup="listbox"
            className="activity-filter-trigger"
            onClick={() =>
              setMenuOpen(
                (current) =>
                  !current,
              )
            }
            type="button"
          >
            <span>
              <SelectedIcon
                aria-hidden="true"
                size={16}
              />

              {
                selectedOption.label
              }
            </span>

            <ChevronDown
              aria-hidden="true"
              className={
                menuOpen
                  ? 'is-open'
                  : ''
              }
              size={16}
            />
          </button>

          <div
            aria-hidden={!menuOpen}
            aria-label="Activity category"
            className={`activity-filter-menu${menuOpen ? ' is-open' : ''}`}
            role="listbox"
          >
              {FILTER_OPTIONS.map(
                (option) => {
                  const Icon =
                    option.icon

                  const selected =
                    option.value ===
                    filter

                  return (
                    <button
                      aria-selected={
                        selected
                      }
                      className={
                        selected
                          ? 'is-selected'
                          : ''
                      }
                      key={
                        option.value
                      }
                      onClick={() => {
                        setFilter(
                          option.value,
                        )

                        setMenuOpen(
                          false,
                        )
                      }}
                      role="option"
                      tabIndex={menuOpen ? 0 : -1}
                      type="button"
                    >
                      <span>
                        <Icon
                          aria-hidden="true"
                          size={16}
                        />

                        {
                          option.label
                        }
                      </span>

                      {selected ? (
                        <Check
                          aria-hidden="true"
                          size={15}
                        />
                      ) : null}
                    </button>
                  )
                },
              )}
          </div>
        </div>
      </div>

      <div className="activity-history-scroll">
        {groupedRows.length > 0 ? (
          groupedRows.map(
            ([key, group]) => (
              <section
                className="activity-history-group"
                key={key}
              >
                <h4>
                  {dateHeading(
                    group[0]
                      .entry
                      .timestamp,
                  )}
                </h4>

                <div>
                  {group.map(
                    (row) => (
                      <article
                        className="activity-history-row"
                        key={
                          row.entry.id
                        }
                      >
                        <div className="activity-history-logo">
                          <LeadWebsiteLogo
                            website={
                              row.website
                            }
                          />
                        </div>

                        <div className="activity-history-copy">
                          <strong>
                            {
                              row.company
                            }
                          </strong>

                          <span className="activity-history-owner">
                            {
                              row.owner ===
                              'Not identified'
                                ? 'Owner not identified'
                                : row.owner
                            }
                          </span>

                          <span className="activity-history-action">
                            <ActivityCategoryIcon
                              category={
                                row.category
                              }
                            />

                            <span>
                              {
                                row.entry
                                  .action
                              }
                            </span>

                            <time
                              dateTime={
                                new Date(
                                  row.entry
                                    .timestamp,
                                )
                                  .toISOString()
                              }
                            >
                              {
                                activityTime(
                                  row.entry
                                    .timestamp,
                                )
                              }
                            </time>
                          </span>
                        </div>
                      </article>
                    ),
                  )}
                </div>
              </section>
            ),
          )
        ) : (
          <div className="activity-history-empty">
            <ListFilter
              aria-hidden="true"
              size={20}
            />

            <strong>
              No matching activity
            </strong>

            <span>
              Try another company,
              owner, or activity
              category.
            </span>
          </div>
        )}
      </div>
    </section>
  )
}

